import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    MenuItem,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import { createSubscriptionPlan } from "../api/subscriptionApi";
import { useNavigate } from "react-router-dom";
import { ROLES, clearSession, hasRole } from "../utils/auth";
import { getApiErrorMessage } from "../utils/apiError";

const CreateSubscriptionPlan = () => {
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        period: "monthly",
        interval: "1",
        name: "",
        amount: "",
        // currency: "",
        description: "",
         features: {
        "Deep Analysis": 5,
        "Visual Analysis": "No",
        "Dashboard Availability": "No",
        "AI Assessment": "None",
        "AI Roadmap": "None",
        "Trainer Videos": "None",
        "Tournaments Ads": "With Ads",
        "External Chess Profiles Analysis": 5,
        "Past Games Analysis": "0 days",
    },
        tierRank: "",
        planType: "paid", // "trial" | "free" | "paid"
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!hasRole(ROLES.SUPERADMIN)) {
            navigate("/unauthorized", { replace: true });
        }
    }, [navigate]);

    const handleFeatureChange = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            features: {
                ...prev.features,
                [key]: value,
            },
        }));
    };

    const featureOptions = {
        "Deep Analysis": [0, 5, 10, 15],
        "Visual Analysis": ["Yes", "No"],
        "Dashboard Availability": ["Yes", "No"],
        "AI Assessment": ["None", "Weekly", "Monthly"],
        "AI Roadmap": ["None", "Weekly", "Monthly"],
        "Trainer Videos": ["None", "Own Instructor", "All Instructors"],
        "Tournaments Ads": ["With Ads", "Without Ads"],
        "External Chess Profiles Analysis": [0, 5, 10, 15],
        "Past Games Analysis": [
            "0 days",
            "1 week",
            "1 month",
            "3 months",
            "1 year",
            "3 years",
        ],
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith("notes.")) {
            const noteKey = name.split(".")[1];
            setFormData((prev) => ({
                ...prev,
                notes: {
                    ...prev.notes,
                    [noteKey]: value,
                },
            }));
        } else if (name === "period") {
            // If Perpetual is selected, set interval to 10
            if (value === "perpetual") {
                setFormData((prev) => ({
                    ...prev,
                    [name]: value,
                    interval: "10",
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    [name]: value,
                    interval: "1",
                }));
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check superadmin permission
        if (!hasRole(ROLES.SUPERADMIN)) {
            alert("Access Denied: You do not have permission to create subscription plans.");
            navigate("/unauthorized", { replace: true });
            return;
        }

        if (!validate()) return
        const isPaid = formData.planType === "paid";
        const payload = {
            period: formData.period === "perpetual" ? "yearly" : formData.period,
            interval: Number(formData.interval),
            name: formData.name,
            amount: isPaid ? Number(formData.amount) : 0,
            // currency: formData.currency,
            description: formData.description,
            features: formData.features,
            tierRank: Number(formData.tierRank),
            isTrialPlan: formData.planType === "trial",
            isFreePlan: formData.planType === "free",
        };

        console.log("Final Payload:", payload);

        try {
            setLoading(true);

            const res = await createSubscriptionPlan(payload);

            console.log("Success:", res);
            if (res?.success) {
                alert(res?.message || "Subscription created successfully 🚀");
                navigate("/manage-subscription");
                return;
            }

            setFormData({
                period: "",
                interval: "1",
                name: "",
                amount: "",
                // currency: "",
                description: "",
                features: {
                    "Deep Analysis": 5,
                    "Visual Analysis": "No",
                    "Dashboard Availability": "No",
                    "AI Assessment": "None",
                    "AI Roadmap": "None",
                    "Trainer Videos": "None",
                    "Tournaments Ads": "With Ads",
                    "External Chess Profiles Analysis": 5,
                    "Past Games Analysis": "0 days",
                },
                tierRank: "",
                planType: "paid",
            });

            alert(res?.message || "Subscription created successfully 🚀");
        } catch (error) {
            console.error("Error:", error);
            alert(getApiErrorMessage(error, "Failed to create subscription plan ❌"));
        } finally {
            setLoading(false);
        }
    };
    
    const countChars = (text) => text.length;

    const countAlphabets = (text) =>
        (text.match(/[A-Za-z]/g) || []).length;

    const isAlphabetic = (text) =>
        /^[A-Za-z\s]+$/.test(text.trim());


    const validate = () => {
        let newErrors = {};

        // Period
        if (!formData.period) {
            newErrors.period = "Period is required";
        }
        // Name
        if (!formData.name) {
            newErrors.name = "Name is required";
        } else if (formData.name.trim().length === 0) {
            newErrors.name = "Only whitespace is not allowed";
        } else if (formData.name.length > 250) {
            newErrors.name = "Name cannot exceed 250 characters";
        }

        // Description (optional, but no whitespace-only)
        if (formData.description && formData.description.length > 0) {
            if (formData.description.trim().length === 0) {
                newErrors.description = "Only whitespace is not allowed";
            } else if (formData.description.length > 250) {
                newErrors.description = "Description cannot exceed 250 characters";
            }
        }
        // Amount (only for paid plans)
        if (formData.planType === "paid") {
            if (!formData.amount) {
                newErrors.amount = "Amount is required";
            } else if (!/^\d+$/.test(formData.amount)) {
                newErrors.amount = "Only positive whole integers are allowed";
            } else if (Number(formData.amount) <= 0) {
                newErrors.amount = "Only positive whole integers are allowed";
            }
        }

        // Tier Rank
        if (formData.tierRank === "") {
            newErrors.tierRank = "Tier Rank is required";
        } else if (!/^\d+$/.test(formData.tierRank)) {
            newErrors.tierRank = "Only positive whole integers are allowed";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNameChange = (e) => {
        const value = e.target.value;

        // ❌ max 250
        if (value.length > 250) {
            setErrors((prev) => ({
                ...prev,
                name: "You have reached the limit",
            }));
            return;
        }

        // ❌ only whitespace
        if (value.length > 0 && value.trim().length === 0) {
            setErrors((prev) => ({
                ...prev,
                name: "Only whitespace is not allowed",
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                name: "",
            }));
        }

        setFormData((prev) => ({
            ...prev,
            name: value,
        }));
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;

        // ❌ max 250
        if (value.length > 250) {
            setErrors((prev) => ({
                ...prev,
                description: "You have reached the limit",
            }));
            return;
        }

        // ❌ only whitespace
        if (value.length > 0 && value.trim().length === 0) {
            setErrors((prev) => ({
                ...prev,
                description: "Only whitespace is not allowed",
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                description: "",
            }));
        }

        setFormData((prev) => ({
            ...prev,
            description: value,
        }));
    };

    const handleTextChange = (field) => (e) => {
        const value = e.target.value;

        // ❌ block >250 chars
        if (value.length > 250) {
            setErrors((prev) => ({
                ...prev,
                [field]: "You have reached the limit",
            }));
            return;
        }

        // ❌ only whitespace
        if (value.length > 0 && value.trim().length === 0) {
            setErrors((prev) => ({
                ...prev,
                [field]: "Only whitespace is not allowed",
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }

        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleIntegerChange = (field) => (e) => {
        let value = e.target.value;

        // ❌ remove anything that is not digit
        value = value.replace(/[^0-9]/g, "");

        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // validation message
        if (value === "") {
            setErrors((prev) => ({
                ...prev,
                [field]: "This field is required",
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#f3f3f3",
                py: 4,
            }}
        >
            <Container maxWidth={false}>
                <Card
                    sx={{
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        border: "1px solid #ddd",
                        bgcolor: "#f7f7f7",
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        {/* Top Header */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
                                alignItems: "start",
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <Box>
                                <Button
                                    variant="contained"
                                    sx={{
                                        background: "linear-gradient(180deg, #c43dd6 0%, #9c27b0 100%)",
                                        textTransform: "uppercase",
                                        fontWeight: 600,
                                        px: 2.5,
                                        py: 1.2,
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                    }}
                                    onClick={() => navigate("/dashboard")}
                                >
                                    Back to Dashboard
                                </Button>
                            </Box>

                            <Typography
                                variant="h5"
                                sx={{
                                    textAlign: "center",
                                    fontWeight: 700,
                                    color: "#1f2937",
                                    mt: { xs: 1, md: 0 },
                                }}
                            >
                                Create Subscription Plan
                            </Typography>
                        </Box>

                        <Typography sx={{ mb: 2, fontSize: "18px", color: "#111827" }}>
                            Subscription Plan
                        </Typography>

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2.2,
                            }}
                        >
                            <TextField
                                fullWidth
                                select
                                label="Period *"
                                name="period"
                                value={formData.period}
                                error={!!errors.period}
                                helperText={errors.period}
                                onChange={handleChange}
                            >
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="yearly">Yearly</MenuItem>
                                <MenuItem value="perpetual">Perpetual</MenuItem>
                            </TextField>

                            <TextField
                                name="name"
                                value={formData.name}
                                label="Name *"
                                onChange={handleNameChange}
                                error={!!errors.name}
                                helperText={errors.name || `${formData.name.length}/250`}
                            />

                            <TextField
                                name="description"
                                value={formData.description}
                                label="Description"
                                onChange={handleDescriptionChange}
                                error={!!errors.description}
                                helperText={errors.description || `${formData.description.length}/250`}
                            />                            

                            {/* <TextField
                                fullWidth
                                select
                                label="Currency *"
                                name="currency"
                                value={formData.currency}
                                onChange={handleChange}
                            >
                                <MenuItem value="INR">INR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                                <MenuItem value="EUR">EUR</MenuItem>
                            </TextField> */}                            

                            <Box
                                sx={{
                                    border: "1px solid #d1d5db",
                                    borderRadius: "8px",
                                    p: 2,
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                <Typography sx={{ fontSize: "16px", fontWeight: 600, mb: 2 }}>
                                    Features
                                </Typography>

                                {Object.keys(featureOptions).map((featureKey) => (
                                    <TextField
                                        key={featureKey}
                                        fullWidth
                                        select
                                        label={featureKey}
                                        value={formData.features[featureKey]}
                                        onChange={(e) =>
                                            handleFeatureChange(featureKey, e.target.value)
                                        }
                                        sx={{ mb: 2 }}
                                    >
                                        {featureOptions[featureKey].map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ))}
                            </Box>

                            <TextField
                                fullWidth
                                type="text"
                                inputMode="numeric"
                                label="Tier Rank *"
                                name="tierRank"
                                value={formData.tierRank}
                                onChange={handleIntegerChange("tierRank")}
                                error={!!errors.tierRank}
                                helperText={errors.tierRank}
                            />
                            <TextField
                                fullWidth
                                select
                                label="Plan Type *"
                                value={formData.planType}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        planType: e.target.value,
                                        amount: e.target.value === "paid" ? prev.amount : "",
                                    }))
                                }
                            >
                                <MenuItem value="trial">Trial Plan</MenuItem>
                                <MenuItem value="free">Free Plan</MenuItem>
                                <MenuItem value="paid">Paid Plan</MenuItem>
                            </TextField>

                            {formData.planType === "paid" && (
                                <TextField
                                    fullWidth
                                    type="text"
                                    inputMode="numeric"
                                    label="Amount *"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleIntegerChange("amount")}
                                    error={!!errors.amount}
                                    helperText={errors.amount}
                                />
                            )}

                            <Box sx={{ mt: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: "#1e88e5",
                                        textTransform: "uppercase",
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1.2,
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : "Create Subscription Plan"}
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default CreateSubscriptionPlan;